/**
 * LA RECHERCHE DU RÉFÉRENTIEL — module PUR, une seule règle.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * ── L'incident du 26/08/2026 ────────────────────────────────────────────────
 *
 * La RQ : « pourquoi quand je cherche glucose, la glycémie n'apparaît pas dans
 * le référentiel, alors que c'est bien un synonyme ? »
 *
 * Elle l'est, deux fois plutôt qu'une : la fiche `GLY` — « Glycémie à jeun » —
 * porte `synonymes: ["Glucose", "Glycémie veineuse"]`, et son paramètre `GLY`
 * s'appelle littéralement « Glucose ».
 *
 * La recherche ne regardait ni l'un ni l'autre. Elle comparait le code, le
 * libellé, la famille, et le CODE des paramètres — jamais leur NOM, jamais les
 * synonymes. Or les synonymes existent précisément pour qu'on trouve un examen
 * sous le mot qu'on emploie, et non sous celui que le laboratoire a retenu :
 * une recherche qui les ignore rend le champ inutile.
 *
 * ── Le même défaut, écrit quatre fois ───────────────────────────────────────
 *
 * La règle était recopiée à QUATRE endroits — la liste des examens, les deux
 * compteurs de la barre de recherche, la liste des paramètres — chacun avec sa
 * propre idée de ce qu'il faut regarder. Aucun n'était faux localement ; aucun
 * n'était complet. C'est le même mécanisme que les libellés « Sur place » /
 * « place » du matin même, et il appelle la même réponse : une règle, un
 * endroit.
 *
 * ── Les accents, qu'on ne tape pas ──────────────────────────────────────────
 *
 * `toLowerCase()` seul laissait « glycemie » sans réponse alors que
 * « Glycémie » existe. On tape rarement les accents dans un champ de
 * recherche, et jamais sur un clavier de téléphone.
 */

/** Insensible aux accents ET à la casse. Reprise de `Selecteur` (`ui.tsx`) :
 *  les deux doivent rapprocher pareil, sous peine de se contredire d'un écran
 *  à l'autre. */
export function sansAccent(v: unknown): string {
  return String(v ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().trim();
}

/** Tout ce qu'un texte cherché peut légitimement rencontrer. `null` et
 *  `undefined` sont tolérés partout : ces champs sont facultatifs en base. */
export interface CibleRecherche {
  code?: string | null;
  libelle?: string | null;
  nom?: string | null;
  famille?: string | null;
  sousFamille?: string | null;
  discipline?: string | null;
  /** Synonymes de l'examen, alias du paramètre — le cœur du sujet. */
  synonymes?: (string | null)[] | string | null;
  alias?: (string | null)[] | string | null;
  /** Les objets liés : paramètres d'un examen, examens d'un paramètre. */
  lies?: { code?: string | null; nom?: string | null; libelle?: string | null }[];
}

/** Aplatit une valeur qui peut être une liste OU une chaîne : `synonymes` est
 *  un tableau sur la fiche et une chaîne sur certains paramètres. Traiter les
 *  deux évite qu'un écran cherche dans l'un et pas dans l'autre. */
function mots(v: (string | null)[] | string | null | undefined): string[] {
  if (Array.isArray(v)) return v.filter(Boolean) as string[];
  const s = String(v ?? '').trim();
  return s ? [s] : [];
}

/**
 * Le texte dans lequel une recherche va chercher. Rendu à part de `correspond`
 * pour être vérifiable : c'est la liste de ce qu'on accepte de trouver, et elle
 * doit pouvoir se lire.
 */
export function champsCherchables(c: CibleRecherche): string[] {
  return [
    c.code, c.libelle, c.nom, c.famille, c.sousFamille, c.discipline,
    ...mots(c.synonymes),
    ...mots(c.alias),
    ...(c.lies || []).flatMap((l) => [l.code, l.nom, l.libelle]),
  ].filter((x): x is string => !!x && String(x).trim() !== '');
}

/**
 * La requête `q` trouve-t-elle cette cible ?
 *
 * ⚠️ Une requête VIDE trouve TOUT. C'est le comportement d'une liste non
 * filtrée : rendre `false` viderait l'écran à l'ouverture, avant qu'on ait
 * tapé quoi que ce soit.
 */
export function correspond(c: CibleRecherche, q: string): boolean {
  const t = sansAccent(q);
  if (!t) return true;
  return champsCherchables(c).some((v) => sansAccent(v).includes(t));
}
